import React, { useMemo, useEffect } from "react";
import {
  FormRadioGroup,
  FormTextarea,
  FormFileUpload
} from "protocol-setup/components/FormComponents";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { postWithAuth } from "protocol-setup/redux/makeFetch";
import {
  defaultErrorMessage,
  groupTestingUrl,
  signoffReminderUrl
} from "protocol-setup/redux/constants";
import {
  OpenErrorNotification,
  OpenSuccessNotification
} from "protocol-setup/components/notification";
import makeFileUploadRequest from "protocol-setup/redux/makeFileUploadRequest";
import ButtonGroupSignoff from "protocol-setup/components/FormComponents/ButtonGroupSignoff";
import { dispatchRefreshEvent, log, useSetupPermission } from "protocol-setup/helpers";
import errorMessages, { supportedFileTypes, COMMENTS_MAX_LENGTH } from "protocol-setup/constants/errorMessages";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import ProtectionElements from 'protocol-setup/helpers/ProtectionElement';
import { calculateFilesToDelete, handleFileDeletions } from "protocol-setup/helpers/fileHelpers";
import { tabNames } from "../orHelpers";
import { yesNoOptions } from "../Lovs";

export const handleSubmitForm = async (
  data,
  requestNumber,
  onSuccessCallback,
  initialFiles = []
) => {
  try {
    const fd = new FormData();
    fd.append(
      "reqStr",
      JSON.stringify({
        approvalStatus: data.isGroupCompletedTesting,
        comment: data.comments,
        requestNumber
      })
    );

    // Handle file uploads
    if (Array.isArray(data.files) && data.files.length > 0) {
      data.files.forEach((file) => {
        if (file instanceof Blob || file instanceof File) {
          const { name } = file;
          fd.append("files", file, name);
        }
      });
    }

    await makeFileUploadRequest(groupTestingUrl, fd, "POST");

    const filesToDelete = calculateFilesToDelete(data.files, initialFiles);
    await handleFileDeletions(filesToDelete);

    if (onSuccessCallback) {
      await onSuccessCallback();
    }

    OpenSuccessNotification({
      message: "Group Testing form successfully submitted.",
      autoHide: true
    });
  } catch (e) {
    OpenErrorNotification({
      message: defaultErrorMessage,
      autoHide: true
    });
  }
};

export const handleSendReminder = async (requestNumber) => {
  try {
    const payload = {
      requestNumber,
      notificationId: "UATGrpTst"
    };
    await postWithAuth(signoffReminderUrl, payload);

    OpenSuccessNotification({
      message: "Group Testing Reminder successfully submitted.",
      autoHide: true
    });
  } catch (e) {
    OpenErrorNotification({
      message: defaultErrorMessage,
      autoHide: true
    });
  }
};

export const groupTestingSchema = yup.object().shape({
  isGroupCompletedTesting: yup.boolean().nullable().required("Required").test(
    'not-null',
    'Required',
    (value) => value !== null
  ),
  comments: yup
    .string()
    .max(COMMENTS_MAX_LENGTH, errorMessages.openRave.groupTestingForm.comments.max)
    .when("isGroupCompletedTesting", ([isGroupCompletedTesting], schema) => {
      return isGroupCompletedTesting === false
        ? schema.required(errorMessages.openRave.groupTestingForm.comments.required)
        : schema.optional();
    }),
  files: yup
    .array()
    .test(
      "fileFormat",
      "Only .doc, .pdf, .msg, .xlsx files are allowed",
      (files) => {
        if (!files || files.length === 0) return true;
        const supportedFormats = supportedFileTypes
        return files.every(
          (file) =>
            !file ||
            supportedFormats.includes(file.name?.split(".").pop().toLowerCase())
        );
      }
    )
    .optional()
});

const convertDataFormat = (formData) => {
  return formData.map((f) => {
    let isGroupCompletedTesting = null;
    if (f?.IS_GROUP_TESTING_COMPLETED_YN === "Y") {
      isGroupCompletedTesting = true;
    } else if (f?.IS_GROUP_TESTING_COMPLETED_YN === "N") {
      isGroupCompletedTesting = false;
    }
    return (
      {
        isGroupCompletedTesting,
        comments: f?.COMMENTS || null,
        files:
          f.uploaded_docs?.map((x) => {
            return { name: x.DOC_FILE_NAME, id: x.DOCUMENT_ID };
          }) || [],
        submitDate: f.SUBMITTED_DT,
        submitBy: f.SUBMITTED_BY_NAME
      }
    )
  })

};

export const getFiles = (formData) => {
  const files = [];
  if (formData) {
    formData.forEach((f) => {
      files.push(...(f.files ?? []));
    })
  }
 
  return files
}

const GroupTestingForm = ({ protocolSetupData, fetchPsData, readOnly }) => {
  const { requestNumber, groupTestDtoList } = protocolSetupData || {};
  const groupTestingData = useMemo(() => {
    let data = [];
    if (groupTestDtoList && groupTestDtoList.length > 0) {
      try {
        data = convertDataFormat(
          JSON.parse(groupTestDtoList[0].userTypeCommentsJson)
        );
      } catch (error) {
        log("Error parsing group test data:", error);
      }
    }
    return data;
  }, [groupTestDtoList]);

  const lastItem = groupTestingData?.[0];
  const lastStatusNo = lastItem?.isGroupCompletedTesting === false;
  
  // If last status was No, clear the input.
  const defaultComments = lastStatusNo ? "" : (lastItem?.comments || "");
  
  // If last status was No, show it in history.
  const commentsHistory = groupTestingData;

  const hasSignOffPermission = useSetupPermission(ProtectionElements.OPEN_SIGNOFF_GROUPTESTING);
  const isSignOffEditable = !readOnly && hasSignOffPermission;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    formState: { isSubmitting, dirtyFields }
  } = useForm({
    resolver: yupResolver(groupTestingSchema),
    defaultValues: {
      isGroupCompletedTesting: lastItem?.isGroupCompletedTesting ?? null,
      comments: defaultComments,
      files: getFiles(groupTestingData)
    }
  });

  useEffect(() => {
    reset({
      isGroupCompletedTesting: lastItem?.isGroupCompletedTesting? true : null,
      comments: '',
      files: getFiles(groupTestingData)
    });
  }, [groupTestingData, reset, lastItem, defaultComments]);

  const onSuccess = async () => {
    await fetchPsData();
    dispatchRefreshEvent();
  };

  // Wrap handleSubmitForm with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback) =>
      handleSubmitForm(data, requestNumber, onSuccessCallback, getFiles(groupTestingData)),
    dirtyFields,
    tabNames.SIGNOFF_GROUP_TESTING,
    "Signoff"
  );

 const onSubmit = (data) => {
    if (readOnly) return;
    const d = { ...data, requestNumber };
    onSubmitWithHistory(d, onSuccess);
  };

  const onReset = () => {
    reset();
  };

  return (
    <div
      className={`form-layout-wrapper bg-transparent ${readOnly ? "read-only" : ""
        }`}
    >
      <form
        id="GroupTestingForm"
        className="bg-transparent"
        onSubmit={handleSubmit(onSubmit)}
        encType="multipart/form-data"
      >
        <div className="form-layout-body px-0">
          <span className="d-none"></span>
          <ul className="row">
            <ul className="col-8 mr-4 mb-0">
              <li className="d-block">
                <FormRadioGroup
                  name="isGroupCompletedTesting"
                  section="openRave"
                  form="groupTestingForm"
                  control={control}
                  options={yesNoOptions}
                  required
                  disabled={!isSignOffEditable}
                  wrapperClassName="mb-3"
                  trigger={trigger}
                />
                <FormTextarea
                  name="comments"
                  section="openRave"
                  form="groupTestingForm"
                  control={control}
                  rows={3}
                  disabled={!isSignOffEditable}
                  wrapperClassName="mb-3"
                  commentsUnderLabel={commentsHistory}
                  data-testid="form-textarea-comments"
                  required={watch("isGroupCompletedTesting") === false}
                  trigger={trigger}
                />
                <FormFileUpload
                  name="files"
                  section="openRave"
                  form="groupTestingForm"
                  control={control}
                  accept=".doc,.docx,.pdf"
                  multiple
                  disabled={!isSignOffEditable}
                />
              </li>
            </ul>
          </ul>
        </div>
        <div className="form-layout-footer px-0 pb-0">
          {isSignOffEditable && (
            <ButtonGroupSignoff
              onSubmit={onSubmit}
              onReset={onReset}
              control={control}
              isSubmitting={isSubmitting}
              onSendReminder={() => {
                handleSendReminder(requestNumber);
              }}
            protectionElement= {ProtectionElements.OPEN_SIGNOFF_GROUPTESTING }
            protectionElementSR= {ProtectionElements.OPEN_SIGNOFF_GROUPTESTING_REMINDER }
            />
          )}
        </div>
      </form>
    </div>
  );
};

export default GroupTestingForm;
